import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaystackButton } from 'react-paystack';
import { toast } from 'react-toastify';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useOrderHistory } from '../context/OrderHistoryContext';
import Loader from '../components/Loader';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const { updateProfile } = useProfile();
  const { addOrderToHistory } = useOrderHistory();

  const [address, setAddress] = useState('');
  const [payment, setPayment] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  // Paystack public key (replace with your real key)
  const paystackPublicKey = 'pk_test_xxxxxxxxxxxxxxxxxxxxxxxx';

  // Paystack config
  const paystackConfig = {
    reference: new Date().getTime().toString(),
    email: user?.email || 'customer@email.com',
    amount: getCartTotal() * 100, // Paystack expects amount in kobo
    publicKey: paystackPublicKey,
  };

  // Handle Paystack payment success
  const handlePaystackSuccess = (reference) => {
    setOrderPlaced(true);
    clearCart();
    updateProfile({ address });
    addOrderToHistory({
      id: Date.now(),
      date: new Date().toLocaleString(),
      items: cart.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price
      })),
      total: getCartTotal(),
      address,
      paymentMethod: 'card',
      paystackRef: reference.reference
    });
    toast.success('Payment successful! Order placed.');
  };

  // Handle order placement for cash
  const handlePlaceOrder = (e) => {
    e.preventDefault();
    if (!address) {
      toast.error('Please enter your delivery address.');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setOrderPlaced(true);
      clearCart();
      updateProfile({ address });
      addOrderToHistory({
        id: Date.now(),
        date: new Date().toLocaleString(),
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        total: getCartTotal(),
        address,
        paymentMethod: 'cash'
      });
      setLoading(false);
      toast.success('Order placed! Pay on delivery.');
    }, 1500);
  };

  if (loading) return <Loader text="Placing your order..." />;

  return (
    <div className="checkout-container" style={{
      background: 'linear-gradient(135deg, #FFF8DC, #f8f9fa)',
      minHeight: '100vh',
      padding: '2rem'
    }}>
      <div className="checkout-container" style={{
        maxWidth: '700px',
        margin: '2rem auto',
        background: 'white',
        borderRadius: '25px',
        boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
        padding: '2.5rem'
      }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: '900',
          color: '#008751',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          🛒 Checkout
        </h1>

        {orderPlaced ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ fontSize: '3rem' }}>✅</div>
            <h2 style={{ color: '#008751', margin: '1rem 0' }}>Order Placed Successfully!</h2>
            <p>Thank you for ordering with TiwaTinwa. Your delicious food is on its way!</p>
            <button
              style={{
                marginTop: '2rem',
                background: 'linear-gradient(135deg, #008751, #fb923c)',
                color: 'white',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '18px',
                fontWeight: '900',
                fontSize: '1.1rem',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/menu')}
            >
              <span style={{ pointerEvents: 'none' }}>🍽️ Back to Menu</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handlePlaceOrder}>
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ color: '#008751', fontWeight: '800', marginBottom: '1rem' }}>Order Summary</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {cart.map(item => (
                  <li key={item.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.7rem',
                    borderBottom: '1px solid #f3f4f6',
                    paddingBottom: '0.5rem'
                  }}>
                    <span style={{ pointerEvents: 'none' }}>{item.name} x {item.quantity}</span>
                    <span style={{ pointerEvents: 'none' }}>₦{(item.price * item.quantity).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: '900',
                fontSize: '1.2rem',
                marginTop: '1rem'
              }}>
                <span>Total</span>
                <span>₦{getCartTotal().toLocaleString()}</span>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '700',
                color: '#008751'
              }}>
                📍 Delivery Address
              </label>
              <textarea
                value={address}
                onChange={e => setAddress(e.target.value)}
                required
                rows={3}
                style={{
                  width: '100%',
                  padding: '1rem',
                  border: '2px solid #e9ecef',
                  borderRadius: '15px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '700',
                color: '#008751'
              }}>
                💳 Payment Method
              </label>
              <div style={{ display: 'flex', gap: '1.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: payment === 'cash' ? '700' : '500',
                  color: payment === 'cash' ? '#008751' : '#6b7280',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="payment"
                    value="cash"
                    checked={payment === 'cash'}
                    onChange={() => setPayment('cash')}
                  />
                  Cash on Delivery
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: payment === 'card' ? '700' : '500',
                  color: payment === 'card' ? '#008751' : '#6b7280',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={payment === 'card'}
                    onChange={() => setPayment('card')}
                  />
                  Card (Paystack)
                </label>
              </div>
              {payment === 'card' && (
                <div style={{ marginTop: '1.5rem' }}>
                  <PaystackButton
                    {...paystackConfig}
                    text="Pay Now with Paystack"
                    onSuccess={handlePaystackSuccess}
                    onClose={() => toast.info('Payment cancelled')}
                    className="paystack-button"
                    disabled={loading}
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || payment === 'card'}
              style={{
                width: '100%',
                background: loading ? '#9ca3af' : 'linear-gradient(135deg, #008751, #fb923c)',
                color: 'white',
                border: 'none',
                padding: '1.2rem',
                borderRadius: '18px',
                fontWeight: '900',
                fontSize: '1.1rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <span style={{ pointerEvents: 'none' }}>
                {loading ? '⏳ Placing Order...' : '✅ Place Order'}
              </span>
            </button>
          </form>
        )}
      </div>
      {/* Responsive styles for checkout page */}
      <style>{`
        .checkout-container {
          box-sizing: border-box;
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
        }
        @media (max-width: 600px) {
          .checkout-container {
            padding: 0.5rem !important;
          }
        }
        .paystack-button {
          width: 100%;
          background: linear-gradient(135deg, #008751, #fb923c);
          color: white;
          border: none;
          padding: 1.2rem;
          border-radius: 18px;
          font-weight: 900;
          font-size: 1.1rem;
          cursor: pointer;
          margin-top: 1rem;
          transition: all 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default CheckoutPage;